<script setup lang="ts">
/**
 * Minutes of AGM — (Multiple) / (Sole Corporate) / (Sole Individual) × AFS(PRS036~038) / UFS(PRS021~023).
 *   kind·variant 별 문구는 템플릿 원문 그대로(AFS=감사인 재선임 포함, UFS=Short notice 문구·감사인 없음).
 */
import CddVarField from '../../../../preset/components/final-dividends/cdd/CddVarField.vue';
import '../../../../preset/components/final-dividends/docs/agmDoc.css';
import AgmCond from '../AgmCond.vue';
import AgmDocHead from '../AgmDocHead.vue';
import AgmMeetingTable from '../AgmMeetingTable.vue';
import AgmSignersPreview from '../AgmSignersPreview.vue';
import { useAgmStudio } from '../agmStudioContext';

defineProps<{ variant: 'afs' | 'ufs'; kind: 'multiple' | 'sole_corporate' | 'sole_individual' }>();
const { model } = useAgmStudio();
</script>

<template>
  <div class="agm-doc">
    <AgmDocHead />

    <!-- 머리 + 서두 -->
    <template v-if="kind === 'multiple'">
      <div class="doc-title">MINUTES OF ANNUAL GENERAL MEETING</div>
      <AgmMeetingTable order="dtv" :upper="variant === 'afs'" with-present />
      <div class="sec-title">CHAIRMAN</div>
      <p class="doc-p">It was agreed that <CddVarField field-key="chairman_name" label="Chairman" /> was appointed Chairman of the Meeting.</p>
      <div class="sec-title">NOTICE</div>
      <p v-if="variant === 'afs'" class="doc-p">Notice of this meeting was taken as read.</p>
      <AgmCond v-else flag="isShortNotice" label="Short notice">
        <p class="doc-p">Notice of this meeting having been issued less than the full prescribed notice period, it was noted that the consent to short notice had been duly obtained from all members entitled to attend and vote at the Meeting pursuant to Section 177(3)(a) of the Companies Act 1967.</p>
        <template #else><p class="doc-p">Notice of this Meeting was taken as read.</p></template>
      </AgmCond>
    </template>
    <template v-else-if="kind === 'sole_corporate'">
      <div class="doc-title">MINUTES BY REPRESENTATIVE OF HOLDING COMPANY</div>
      <div class="doc-sub">RELATING TO PROCEEDINGS OF SUBSIDIARY COMPANY</div>
      <div class="doc-sub">UNDER SECTION 179(6) OF THE COMPANIES ACT 1967</div>
      <p class="doc-p">
        I, <CddVarField field-key="member_representative_name" label="Corporate Representative" />, the undersigned, the duly appointed representative of
        <CddVarField field-key="member_name" label="Holding Company (Sole Member)" />, the holding company beneficially entitled to the whole of the issued share capital of the Company, hereby confirm that the Annual General Meeting of the Company was deemed held on
        <CddVarField field-key="agm_date" label="AGM Date" /><template v-if="variant === 'afs'"> at <CddVarField field-key="agm_time" label="AGM Time" /></template>
        and that the following resolutions were duly passed pursuant to Section 179(6) of the Companies Act 1967:
      </p>
    </template>
    <template v-else>
      <div class="doc-title">MINUTES OF ANNUAL GENERAL MEETING</div>
      <p class="doc-p">
        I, <CddVarField field-key="member_name" label="Sole Member" />, the undersigned, being the Sole Member of the Company beneficially entitled to the whole of the issued shares of the Company do hereby pass the following resolutions pursuant to Section 184G of the Companies Act 1967 and agree that the said resolutions shall, for all purposes, be as valid and effective as if the same had been passed at the Annual General Meeting of the Company duly convened and held on
        <CddVarField field-key="agm_date" label="AGM Date" />:
      </p>
    </template>

    <!-- 재무제표 -->
    <template v-if="variant === 'afs'">
      <div class="sec-title">AUDITED FINANCIAL STATEMENTS</div>
      <p class="doc-p">IT WAS RESOLVED THAT the Audited Financial Statements of the Company for the financial year ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" /> together with the Directors’ Statement and the Auditors’ Report thereon, as presented, be and are hereby received and adopted.</p>
    </template>
    <template v-else>
      <div class="sec-title">UNAUDITED FINANCIAL STATEMENTS</div>
      <p class="doc-p">IT WAS RESOLVED THAT the Unaudited Financial Statements of the Company for the financial year ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" /> together with the Directors’ Statement thereon be received and adopted.</p>
    </template>

    <AgmCond flag="hasDirectorsRemuneration" label="Directors’ fee / remuneration">
      <div class="sec-title">SPECIAL BUSINESS</div>
      <div class="doc-sub">ORDINARY RESOLUTION</div>
      <div class="doc-sub">DIRECTORS’ FEE AND/OR DIRECTORS’ REMUNERATION</div>
      <p class="doc-p">IT WAS RESOLVED THAT the directors’ fee and/or directors’ remuneration, if any, for the financial year ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" />, as stated in the financial statements be {{ variant === 'afs' ? 'and are hereby approved' : 'approved' }}.</p>
    </AgmCond>

    <AgmCond flag="hasRetiringDirectors" label="Re-election of retiring directors">
      <div class="sec-title">RETIREMENT OF DIRECTORS</div>
      <p class="doc-p">IT WAS RESOLVED THAT all the Directors who retire in accordance with the Company’s Constitution be re-elected as Directors of the Company.</p>
    </AgmCond>

    <template v-if="variant === 'afs'">
      <div class="sec-title">RE-APPOINTMENT OF AUDITORS</div>
      <p class="doc-p">IT WAS RESOLVED THAT Messrs <CddVarField field-key="auditor_name" label="Auditor" />, having indicated their willingness to accept re-appointment, be and are hereby re-appointed Auditors of the Company, to hold office until the conclusion of the next Annual General Meeting, at a fee to be determined by the Board of Directors, pursuant to Section 205 of the Companies Act 1967.</p>
    </template>

    <!-- 맺음 + 서명 -->
    <template v-if="kind === 'multiple'">
      <div class="sec-title">CONCLUSION</div>
      <p class="doc-p">{{ variant === 'afs'
        ? 'There being no other business, the Meeting terminated with a vote of thanks to the Chairman of the Meeting.'
        : 'There being no other business, the Meeting was terminated with a vote of thanks to the Chairman.' }}</p>
      <p class="doc-p">Confirmed by:</p>
      <AgmSignersPreview doc-kind="minutes_multiple" />
      <div class="doc-p">Chairman</div>
    </template>
    <template v-else-if="kind === 'sole_corporate'">
      <p class="doc-p">Signed as a correct record:</p>
      <AgmSignersPreview doc-kind="minutes_sole_corporate" />
      <div class="doc-p">(Corporate Representative of <CddVarField field-key="member_name" label="Holding Company (Sole Member)" />)</div>
    </template>
    <template v-else>
      <p class="doc-p">Confirmed by:</p>
      <AgmSignersPreview doc-kind="minutes_sole_individual" />
      <div class="doc-p">Sole Member</div>
    </template>
  </div>
</template>
